import React, { useEffect, useRef, useState } from 'react';

/**
 * Manager Override PIN keypad.
 *
 * Opens when an operator scans an OH-<15 digits> barcode in the picking,
 * pressing, or packing portal. Manager types their existing 4-digit portal
 * PIN. On successful verify, `onVerified(managerInfo)` is called with:
 *   { manager_user_id, first_name, last_name, manager_name }
 *
 * The parent portal then arms the override for the next SKU the operator
 * touches. See PressingOrderBoard / PickingPortal / QCBoard.
 *
 * PIN keypad UI mirrors PressingPortalLogin so it feels the same as PIN
 * entry the operator already knows — 3×4 grid + Clear + Enter.
 */
export default function ManagerOverrideModal({
    open,
    barcode,
    fetchFn,          // authenticated fetch used by the portal shell
    onClose,
    onVerified,
}) {
    const [pin, setPin]         = useState('');
    const [err, setErr]         = useState(null);
    const [busy, setBusy]       = useState(false);
    const dialogRef             = useRef(null);

    // Reset state on open
    useEffect(() => {
        if (open) {
            setPin('');
            setErr(null);
            setBusy(false);
        }
    }, [open]);

    // ESC to close
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    const submitRef = useRef(null);
    const tap = (digit) => {
        if (busy) return;
        setErr(null);
        setPin(p => (p + String(digit)).slice(0, 5));
    };
    const clear = () => { setPin(''); setErr(null); };
    const submit = async () => {
        if (busy) return;
        if (pin.length < 4 || pin.length > 5) { setErr('PIN must be 4 or 5 digits'); return; }
        setBusy(true); setErr(null);
        try {
            const res = await fetchFn('/api/portal/manager-override/verify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ barcode, pin }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setErr(data?.detail || 'Verify failed');
                setPin('');
                setBusy(false);
                return;
            }
            onVerified?.({
                manager_user_id: data.manager_user_id,
                first_name: data.first_name,
                last_name:  data.last_name,
                manager_name: data.manager_name,
            });
        } catch (e) {
            setErr(e.message || 'Network error');
            setPin('');
            setBusy(false);
        }
    };
    submitRef.current = submit;

    // Auto-submit on the 5th digit only. PINs are 4 OR 5 digits, so we can't
    // fire at 4 without breaking 5-digit users — they'd get a wrong-PIN
    // rejection before typing their last digit. 4-digit users press Enter.
    useEffect(() => {
        if (!open) return;
        if (pin.length === 5 && !busy) submitRef.current?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pin, open, busy]);

    if (!open) return null;

    return (
        <div className="mgr-ovr-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
            <div ref={dialogRef} className="mgr-ovr-dialog" role="dialog" aria-modal="true">
                <div className="mgr-ovr-head">
                    <div className="mgr-ovr-eyebrow">Manager Override</div>
                    <div className="mgr-ovr-title">Enter Manager PIN</div>
                    <div className="mgr-ovr-sub">
                        Barcode <code>{barcode}</code>
                    </div>
                </div>

                {/* Single wide input, mirrors PressingPortalLogin. Multi-dot
                    display wrapped on iPhone once we allowed 5 digits. */}
                <div className="mgr-ovr-pin-single">
                    <input
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]{4,5}"
                        maxLength={5}
                        value={pin}
                        onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 5))}
                        placeholder=""
                        autoComplete="off"
                        disabled={busy}
                        aria-label="Manager PIN"
                        className="mgr-ovr-pin-input"
                    />
                </div>

                {err && <div className="mgr-ovr-error">{err}</div>}

                <div className="mgr-ovr-keypad">
                    {[1,2,3,4,5,6,7,8,9].map(n => (
                        <button key={n} className="mgr-ovr-key" onClick={() => tap(n)} disabled={busy}>{n}</button>
                    ))}
                    <button className="mgr-ovr-key mgr-ovr-key--clear" onClick={clear} disabled={busy}>Clear</button>
                    <button className="mgr-ovr-key" onClick={() => tap(0)} disabled={busy}>0</button>
                    <button className="mgr-ovr-key mgr-ovr-key--enter" onClick={submit} disabled={busy || pin.length < 4 || pin.length > 5}>
                        {busy ? '…' : 'Enter'}
                    </button>
                </div>

                <button className="mgr-ovr-cancel" onClick={onClose} disabled={busy}>Cancel</button>
            </div>
        </div>
    );
}
